import { Inject, Injectable } from '@angular/core';

import { ReplaySubject } from 'rxjs';

import {
    CommonsConfigEnvironment,
    CONFIG_ENVIRONMENT,
    LayerTypeEnum,
    PcvizBaseDriver,
    PcvizCollageCloudDriver,
    PcvizComputeBoundingVisitor,
    PcvizHighlightScans,
    PcvizInsider2D,
    PcvizLayer,
    PcvizMagnetAPI,
    PcvizPointVisibilityVisitor,
    PcvizProject,
    PcvizPropertySet,
    PcvizSelectionObject,
    PcvizUpdateVisibilityVisitor,
    PcvizUtils,
} from '@mirage-projects/cw-models';

import { DataAccessPcvizService } from '../data-access/data-access-pcviz.service';
import { DbPcvizService } from '../db/db-pcviz.service';

/** Service we use to make calls to the utils module of core */
@Injectable({ providedIn: 'root' })
export class UtilsPcvizService {
    readonly DRIVERS = ['da.project', 'da.layer', 'da.category', 'da.singlePart', 'da.multiPart', 'da.job', 'da.collage', 'da.site'];

    collageCloudDriver!: PcvizCollageCloudDriver;

    private onControllersInitializedSubject = new ReplaySubject<void>();
    onControllersInitialized$ = this.onControllersInitializedSubject.asObservable();

    constructor(
        private dbPcvizService: DbPcvizService,
        private dataAccessPcvizService: DataAccessPcvizService,
        @Inject(CONFIG_ENVIRONMENT) private configEnvironment: CommonsConfigEnvironment
    ) {}

    configureDrivers(token: string | null, authType: string, isConnectingFromMagnet: boolean, coreEntryPoints: any): void {
        if (!this.collageCloudDriver) {
            // this driver is used in a few places and it can be replaced by the particular driver according to the function used.
            this.collageCloudDriver = this.dataAccessPcvizService.createCollageCloudDriver();
            const magnetAPI = this.getMagnetApi();
            magnetAPI.setAPI(this.configEnvironment.API_CONFIG_CORE);
            magnetAPI.setConnectingFromMagnet(isConnectingFromMagnet);
            magnetAPI.setAuthorizationToken(token);
            magnetAPI.setAuthorizationType(`${authType} `);

            this.configureDriver(this.collageCloudDriver, coreEntryPoints);
            this.DRIVERS.forEach((idDriver) => {
                const driver = this.dataAccessPcvizService.getDriver(idDriver);
                this.configureDriver(driver, coreEntryPoints);
            });

            this.initControllers();
        }
    }

    configureDriver(driver: PcvizBaseDriver, coreEntryPoints: any): void {
        driver.setServerUrl(
            this.configEnvironment.port
                ? `${this.configEnvironment.baseURL}:${this.configEnvironment.port}`
                : this.configEnvironment.baseURL
        );
        driver.setStorageUrl(this.configEnvironment.API_CONFIG_CORE.storageUrl);
        const entryPoints = coreEntryPoints;
        driver.setCurrentEntryPoints(entryPoints);
        const MagnetAPI = this.getMagnetApi();
        driver.setMagnetAPI(MagnetAPI);
    }

    createPropertySet(): PcvizPropertySet {
        return new PointCloudViz.Utils.PropertySet();
    }

    getUtils(): PcvizUtils {
        return PointCloudViz.Utils;
    }

    getMagnetApi(): PcvizMagnetAPI {
        return PointCloudViz.Utils.MagnetAPI;
    }

    getCRSUtils(): typeof PointCloudViz.Utils.CRSUtils {
        return PointCloudViz.Utils.CRSUtils;
    }

    getMathUtils(): typeof PointCloudViz.Utils.MathUtils {
        return PointCloudViz.Utils.MathUtils;
    }

    getShaderUtils(): typeof PointCloudViz.Utils.ShaderUtils {
        return PointCloudViz.Utils.ShaderUtils;
    }

    getImageUtils(): typeof PointCloudViz.Utils.ImageUtils {
        return PointCloudViz.Utils.ImageUtils;
    }

    getTimeStampCache(): typeof PointCloudViz.Utils.TimeStampCache {
        return PointCloudViz.Utils.TimeStampCache;
    }

    getSelectionObject(): typeof PointCloudViz.Utils.SelectionObject {
        return PointCloudViz.Utils.SelectionObject;
    }

    getHighlightScans(): typeof PointCloudViz.Utils.HighlightScans {
        return PointCloudViz.Utils.HighlightScans;
    }

    getHighlightScansInstance(): PcvizHighlightScans {
        return new PointCloudViz.Utils.HighlightScans();
    }

    getUpdateVisibilityVisitor(): typeof PointCloudViz.Utils.UpdateVisibilityVisitor {
        return PointCloudViz.Utils.UpdateVisibilityVisitor;
    }

    getUpdateVisibilityVisitorInstance(): PcvizUpdateVisibilityVisitor {
        return new PointCloudViz.Utils.UpdateVisibilityVisitor();
    }

    getInsider2D(): typeof PointCloudViz.Utils.Insider2D {
        return PointCloudViz.Utils.Insider2D;
    }

    getInsider2DInstance(
        vertex: any,
        nearPoints: any,
        farPoints: any,
        transformMatrix: any,
        type: string,
        boundingBox?: any,
        layersMap?: any
    ): PcvizInsider2D {
        return new PointCloudViz.Utils.Insider2D(vertex, nearPoints, farPoints, transformMatrix, type, boundingBox, layersMap);
    }

    getPointVisibilityVisitor(): typeof PointCloudViz.Utils.PointVisibilityVisitor {
        return PointCloudViz.Utils.PointVisibilityVisitor;
    }

    getPointVisibilityVisitorInstance(): PcvizPointVisibilityVisitor {
        return new PointCloudViz.Utils.PointVisibilityVisitor();
    }

    createComputeBoundingVisitor(): PcvizComputeBoundingVisitor {
        return new PointCloudViz.Utils.ComputeBoundingVisitor();
    }

    updateProjectConfiguration(project: PcvizProject): Promise<void> {
        this.collageCloudDriver.setProjectId(project.getId());
        return this.collageCloudDriver.refreshProjectData(project.getId());
    }

    createSelectionObjectByLayer(layer: PcvizLayer): PcvizSelectionObject {
        let selectionObject: PcvizSelectionObject;
        switch (layer.getType()) {
            case LayerTypeEnum.pointCloud:
                selectionObject = new PointCloudViz.Utils.PointCloudSelectionObject();
                break;
            case LayerTypeEnum.vectorLayer:
                selectionObject = new PointCloudViz.Utils.VectorLayerSelectionObject();
                break;
            case LayerTypeEnum.rasterLayer:
                selectionObject = new PointCloudViz.Utils.RasterLayerSelectionObject();
                break;
            case LayerTypeEnum.layerGroup:
                selectionObject = new PointCloudViz.Utils.LayerGroupSelectionObject();
                break;
            default:
                throw new Error('Invalid Type');
        }
        selectionObject.setLayer(layer);
        return selectionObject;
    }

    /* istanbul ignore next */
    isTechnicalPreview(): boolean {
        return this.getMagnetApi().isTechnicalPreview();
    }

    /* istanbul ignore next */
    setTechnicalPreview(enabled: boolean): void {
        this.getMagnetApi().setTechnicalPreview(enabled);
    }

    private initControllers(): void {
        this.dbPcvizService.registerController('Units', PointCloudViz.DB.UnitController);
        this.dbPcvizService.registerController('Upload', PointCloudViz.DB.UploadController);
        this.dbPcvizService.registerController('Object', PointCloudViz.DB.ObjectController);
        this.dbPcvizService.registerController('Layer', PointCloudViz.DB.LayerController);
        this.dbPcvizService.registerController('SelectionObject', PointCloudViz.DB.SelectionController);
        this.dbPcvizService.registerController('Symbol', PointCloudViz.DB.SymbolController);
        this.dbPcvizService.registerController('View', PointCloudViz.DB.ViewController);
        this.dbPcvizService.registerController('UserPreferences', PointCloudViz.DB.UserPreferencesController);
        this.dbPcvizService.registerController('Application', PointCloudViz.DB.ApplicationController);
        this.dbPcvizService.registerController('Mount', PointCloudViz.DB.MountController);
        this.dbPcvizService.registerController('BlobStorage', PointCloudViz.DB.BlobStorageController);
        this.dbPcvizService.registerController('Error', PointCloudViz.DB.ErrorController);
        this.dbPcvizService.registerController('ImageSelection', PointCloudViz.DB.ImageSelectionController);
        this.dbPcvizService.registerController('Workspace', PointCloudViz.DB.WorkspaceController);
        this.dbPcvizService.registerController('ModelLibrary', PointCloudViz.DB.ModelLibraryController);
        this.dbPcvizService.registerController('Configuration', PointCloudViz.DB.ConfigurationController);
        this.dbPcvizService.registerController('OverwriteSymbology', PointCloudViz.DB.OverwriteSymbologyController);
        this.dbPcvizService.registerController('ScanSelection', PointCloudViz.DB.ScanSelectionController);
        this.dbPcvizService.registerController('C2CLink', PointCloudViz.DB.C2CLinkManager);
        this.dbPcvizService.registerController('PointVisibilityManager', PointCloudViz.DB.PointVisibilityManagerController);
        this.dbPcvizService.registerController('Plugins', PointCloudViz.DB.PluginsController);
        this.dbPcvizService.registerController('OverwriteVisibility', PointCloudViz.DB.OverwriteVisibilityController);

        this.onControllersInitializedSubject.next();
    }
}
